import { type FragmentType, useFragment as readFragment } from '../gql';
import {
  TaxonomyNodeFieldsFragmentDoc,
  type TaxonomyTreeQuery,
} from '../gql/graphql';

type TaxonomyNodeWithChildren = FragmentType<
  typeof TaxonomyNodeFieldsFragmentDoc
> & {
  children?: readonly TaxonomyNodeWithChildren[] | null;
};

/** Minimal level shape the walker needs: an ordering `id` and a level `tag`. */
export type TaxonomyTreeWalkLevel = { id: number; tag?: string | null };

/**
 * Base parent-linked selection-list item: an id + display names, plus one entry
 * per ancestor level keyed by that ancestor's level tag (e.g. a city_core item
 * carries `region`/`country`/`metro_area`; a rics_k400 item carries
 * `rics_k10`/`rics_k50`). Callers extend it with their own fields (`label`,
 * `nodeCount`, `rawTitles`, …) via `buildItem`.
 */
export type ParentLinkedSelectionItem = {
  id: string;
  shortName: string;
  longName: string;
  [ancestorTagOrExtra: string]: string | number | string[] | undefined;
};

/** The id/name/children a single tree node exposes to the walker. */
type AccessedNode<TNode> = {
  id: string | number;
  name: string | null | undefined;
  /** Explicit level depth when the node carries one (TreeData); else positional. */
  depth?: number;
  children?: readonly TNode[] | null;
};

/**
 * Shared tree → per-level selection-list walk. Flattens a taxonomy tree into
 * lists keyed by each level's tag (depth → `levels` sorted by id), parent-linking
 * every item by ancestor tag. Blank or literal-"empty" nodes — which the backend
 * emits at deep geo levels where it has no data — are skipped together with their
 * subtree, and ids are deduped per level.
 *
 * Source-shape-agnostic so a single implementation backs both the dashboard
 * (`treeToParentLinkedSelectionLists`, over normalized `TreeData`) and the
 * dash-meta resolvers (`taxonomyTreeToSelectionLists`, over the raw
 * `TaxonomyTree` GraphQL payload): callers supply `accessNode` to read a node's
 * id/name/children and `buildItem` to add their own fields.
 */
export const walkTaxonomyTreeToSelectionLists = <
  TNode,
  TItem extends ParentLinkedSelectionItem,
  TLevel extends TaxonomyTreeWalkLevel,
>({
  rootNodes,
  levels,
  accessNode,
  buildItem,
  keyForLevel = (level) => level.tag ?? null,
}: {
  rootNodes: readonly TNode[];
  levels: readonly TLevel[];
  accessNode: (node: TNode) => AccessedNode<TNode>;
  buildItem: (
    base: { id: string; shortName: string; longName: string },
    node: TNode
  ) => TItem;
  // How each level maps to its output key (and ancestor-link field). Defaults to
  // the BE level tag (right for rics/geo); role passes an id-based resolver so it
  // doesn't depend on the BE tag strings (see treeToRoleSelectionLists).
  keyForLevel?: (level: TLevel) => string | null;
}): Partial<Record<string, TItem[]>> => {
  const keyByDepth = [...levels].sort((a, b) => a.id - b.id).map(keyForLevel);

  const result: Partial<Record<string, TItem[]>> = {};
  const seenIds = new Map<string, Set<string>>();

  const visit = (
    nodes: readonly TNode[],
    ancestors: ReadonlyArray<{ tag: string; rawId: string }>,
    recursionDepth: number
  ): void => {
    for (const node of nodes) {
      const accessed = accessNode(node);

      const normalizedName = String(accessed.name ?? '')
        .trim()
        .toLowerCase();
      if (normalizedName === '' || normalizedName === 'empty') {
        continue;
      }

      const depth = accessed.depth ?? recursionDepth;
      const rawId = String(accessed.id);
      const key = keyByDepth[depth];

      if (key != null) {
        let ids = seenIds.get(key);
        if (!ids) {
          ids = new Set();
          seenIds.set(key, ids);
          result[key] = [];
        }

        if (!ids.has(rawId)) {
          ids.add(rawId);

          const name = String(accessed.name);
          const item = buildItem(
            { id: rawId, shortName: name, longName: name },
            node
          );
          for (const ancestor of ancestors) {
            (item as ParentLinkedSelectionItem)[ancestor.tag] = ancestor.rawId;
          }

          result[key]?.push(item);
        }
      }

      if (accessed.children && accessed.children.length > 0) {
        visit(
          accessed.children,
          key != null ? [...ancestors, { tag: key, rawId }] : ancestors,
          depth + 1
        );
      }
    }
  };

  visit(rootNodes, [], 0);
  return result;
};

export type TaxonomyTreeSelectionListItem = {
  id: string;
  shortName: string;
  longName: string;
  label: string;
  // Each ancestor level's raw id, keyed by that ancestor's level tag — e.g. a
  // city_core item carries `region`, `country` and `metro_area`; a rics_k400
  // item carries `rics_k10` and `rics_k50`.
  [ancestorLevelTag: string]: string | undefined;
};

export type TaxonomyTreeSelectionLists = Partial<
  Record<string, TaxonomyTreeSelectionListItem[]>
>;

/**
 * Flattens a raw TaxonomyTree GraphQL payload into per-level selection lists
 * keyed by the tree's level tags (region / country / metro_area / city_core, or
 * rics_k10 / rics_k50 / rics_k400), each item parent-linked by ancestor tag.
 *
 * Server-side adapter over {@link walkTaxonomyTreeToSelectionLists}: unmasks the
 * `TaxonomyNodeFields` fragment per node so the dash-meta AI-search resolvers
 * resolve names against the same entries the dashboard pickers display.
 */
export const taxonomyTreeToSelectionLists = (
  taxonomy: TaxonomyTreeQuery['taxonomy'] | null | undefined
): TaxonomyTreeSelectionLists => {
  if (!taxonomy) {
    return {};
  }

  return walkTaxonomyTreeToSelectionLists<
    TaxonomyNodeWithChildren,
    TaxonomyTreeSelectionListItem,
    TaxonomyTreeQuery['taxonomy']['levels'][number]
  >({
    rootNodes: taxonomy.nodes,
    levels: taxonomy.levels,
    accessNode: (node) => {
      const fields = readFragment(TaxonomyNodeFieldsFragmentDoc, node);
      return { id: fields.id, name: fields.name, children: node.children };
    },
    buildItem: (base) => ({ ...base, label: base.shortName }),
  });
};
